import { useSyncExternalStore } from 'react';
import { z } from 'zod';

import { type Mode } from '@libraries/typescript/api_client';

const STORAGE_KEY = 'phoebe-admin-mode';
const CHANNEL_NAME = 'phoebe-admin-mode';

const modeSchema = z.enum(['live', 'sandbox']);

function parseMode(value: unknown): Mode {
    const result = modeSchema.safeParse(value);
    return result.success ? result.data : 'live';
}

let currentMode: Mode | null = null;
let channel: BroadcastChannel | null = null;
let storageHandler: ((event: StorageEvent) => void) | null = null;
const listeners = new Set<() => void>();

function readStoredMode(): Mode {
    let storedMode: string | null = null;
    try {
        storedMode = localStorage.getItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable (private browsing, etc.)
    }

    return parseMode(storedMode);
}

function ensureCurrentMode(): Mode {
    if (currentMode === null) {
        currentMode = readStoredMode();
    }

    return currentMode;
}

function emitChange() {
    for (const listener of listeners) {
        listener();
    }
}

function activate() {
    ensureCurrentMode();

    if (channel !== null) {
        return;
    }

    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
        const newMode = parseMode(event.data);
        if (newMode !== currentMode) {
            currentMode = newMode;
            emitChange();
        }
    };

    storageHandler = (event) => {
        if (event.key === STORAGE_KEY && event.newValue !== null) {
            const newMode = parseMode(event.newValue);
            if (newMode !== currentMode) {
                currentMode = newMode;
                emitChange();
            }
        }
    };
    window.addEventListener('storage', storageHandler);
}

function deactivate() {
    currentMode = null;

    if (channel) {
        channel.close();
        channel = null;
    }

    if (storageHandler) {
        window.removeEventListener('storage', storageHandler);
        storageHandler = null;
    }
}

export const adminModeStore = {
    getMode(): Mode {
        return ensureCurrentMode();
    },
    setMode(mode: Mode) {
        const previousMode = ensureCurrentMode();
        if (mode === previousMode) {
            return;
        }

        currentMode = mode;
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // localStorage unavailable (private browsing, quota exceeded, etc.)
        }
        channel?.postMessage(mode);
        emitChange();
    },
    subscribe(listener: () => void) {
        activate();
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
            if (listeners.size === 0) {
                deactivate();
            }
        };
    },
};

export function useAdminMode(): Mode {
    return useSyncExternalStore(
        adminModeStore.subscribe,
        adminModeStore.getMode,
    );
}
